import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { PageHero } from "./ContentPage";
import { PostCard } from "../components/home/SectionsB";
import { Container } from "../components/Shared";
import { Reveal } from "../components/Motion";
import { api } from "../lib/api";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";
import { useAlternates } from "../lib/alt";
import { R } from "../lib/routes";

const Chip = ({ active, onClick, children, id }) => (
  <button onClick={onClick} data-testid={id} className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${active ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--line)] text-[var(--ink)] hover:border-[var(--ink)]"}`}>{children}</button>
);

export default function BlogList({ doc, alternates = [] }) {
  const { t, lang } = useLang();
  const { slug } = useParams();
  const { setAlternates } = useAlternates();
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(slug || "");
  const [postLang, setPostLang] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [loading, setLoading] = useState(true);
  const catName = cats.find((c) => c.slug === cat)?.name;
  const path = doc?.path || `/category/${slug}/`;
  useSeo({ title: catName ? `${catName} – Blog Translinked` : doc?.meta_title || "Blog – Translinked", description: doc?.meta_description || t.blog.lede, path, lang, alternates });
  useEffect(() => { if (!doc) setAlternates(["es", "en", "de"].map((l) => ({ lang: l, path: R.blog[l] }))); }, [doc, setAlternates]);
  useEffect(() => { api.get("/categories").then((r) => setCats(Array.isArray(r.data) ? r.data : [])).catch(() => setCats([])); }, []);
  useEffect(() => {
    setLoading(true);
    const h = setTimeout(() => {
      api.get("/posts", { params: { category: cat || undefined, lang: postLang || undefined, q: q || undefined, page: 1, limit: 12 } })
        .then((r) => setData({ items: r.data?.items || [], total: r.data?.total ?? 0, page: 1 }))
        .catch(() => setData({ items: [], total: 0, page: 1 }))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(h);
  }, [cat, postLang, q]);
  const more = () => {
    const page = data.page + 1;
    api.get("/posts", { params: { category: cat || undefined, lang: postLang || undefined, q: q || undefined, page, limit: 12 } })
      .then((r) => setData((d) => ({ ...d, items: [...d.items, ...(r.data?.items || [])], page })))
      .catch(() => {});
  };
  return (
    <main data-testid="blog-page">
      <PageHero eyebrow={t.blog.eyebrow} title={catName || t.blog.title} lede={`${t.blog.lede} ${t.blog.langNote}`} />
      <Container className="pb-24">
        <Reveal className="flex flex-col gap-6 border-y border-[var(--line)] py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <label className="flex w-full items-center gap-3 rounded-full border border-[var(--line)] bg-white px-5 py-3 md:max-w-sm">
              <Search className="h-4 w-4 text-[var(--muted)]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.blog.search} className="w-full bg-transparent text-sm outline-none" data-testid="blog-search-input" aria-label={t.blog.search} />
            </label>
            <div className="flex gap-2">
              {["", "es", "en", "de"].map((l) => <Chip key={l || "all"} active={postLang === l} onClick={() => setPostLang(l)} id={`blog-lang-${l || "all"}`}>{l ? l.toUpperCase() : t.blog.allLangs}</Chip>)}
            </div>
          </div>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 md:flex-wrap" data-lenis-prevent>
            <Chip active={!cat} onClick={() => setCat("")} id="blog-cat-all">{t.blog.all}</Chip>
            {cats.map((c) => <Chip key={c.slug} active={cat === c.slug} onClick={() => setCat(c.slug)} id={`blog-cat-${c.slug}`}><span className="whitespace-nowrap">{c.name}</span></Chip>)}
          </div>
        </Reveal>
        {!loading && data.items.length === 0 && <p className="py-20 text-center text-[var(--muted)]" data-testid="blog-empty">{t.blog.empty}</p>}
        <div className="mt-14 grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
          {data.items.map((p) => <PostCard key={p.path} p={p} lang={lang} t={t} />)}
        </div>
        {data.items.length < data.total && (
          <div className="mt-16 flex justify-center"><button onClick={more} className="rounded-full border border-[var(--ink)] px-8 py-3.5 text-sm transition-colors hover:bg-[var(--ink)] hover:text-white" data-testid="blog-load-more">{t.blog.loadMore} ({data.items.length}/{data.total})</button></div>
        )}
      </Container>
    </main>
  );
}
